import { usePopoverState, Typography, PopoverDisclosure, Stack, DropdownMenu } from '@ui-components/neo.tax';
import { ReactComponent as SelectorIcon } from '@ui-components/neo.tax/dist/icons/selector.svg';
import { useMemo } from 'react';

import { SortDirection } from '@neo.tax/shared/graphql';
import { FilterSortDataType } from '@neo.tax/shared/utils';

import { Sorting, SortingState } from './hooks';
import {
  StyledDropdownButton,
  StyledPopover,
  StyledAddConditionButton,
  StyledMenuItemInnerContainer,
  StyledTitle,
  StyledPopoverTitle,
  StyledPopoverTitleSemibold,
  StyledPlusIcon,
  StyledDraggableMenu,
  StyledSortIcon,
  StyledDraggableMenuItem,
  StyledConfigDropdownMenu,
  StyledResetButton,
  StyledDeleteButton,
  StyledTrashBin,
} from './sort-popover.styled';
import { SortDirectionDropdownMenu } from './SortDirectionDropdownMenu';

export interface SortPopoverProps<ColumnName extends string> extends SortingState<ColumnName> {
  /**
   * A description of what will be sorted, as a plural noun. Appears as "Only
   * show `<itemsDescription>`…".
   */
  itemsDescription: string;
}

/**
 * A button which opens a popover containing sorting controls for a table or
 * other set of data.
 */
export const SortPopover = <ColumnName extends string>({
  columns,
  itemsDescription,
  resetSorts,
  setSorts,
  sorts,
}: SortPopoverProps<ColumnName>): React.ReactElement => {
  const state = usePopoverState({ gutter: 6, placement: 'bottom-start' });
  const sortCount = sorts.length;

  const fieldTypeForColumn = useMemo(() => {
    const fieldTypesByColumnName = new Map<ColumnName, FilterSortDataType>(columns.map((cd) => [cd.name, cd.dataType]));
    // We don't expect to ever not have an entry in the map, but if we ever do,
    // provide a sensible fallback.
    return (columnName: ColumnName): FilterSortDataType =>
      fieldTypesByColumnName.get(columnName) || FilterSortDataType.TEXT;
  }, [columns]);

  const handleAddSort = (sortField: ColumnName): void => {
    const direction = [FilterSortDataType.TEXT, FilterSortDataType.ENUM].includes(fieldTypeForColumn(sortField))
      ? SortDirection.Asc
      : SortDirection.Desc;
    const newSorting = [...sorts, { id: Date.now().toString(), column: sortField, direction }];
    setSorts(newSorting);
  };

  const handleDeleteSort = (id: string): void => {
    const newSorting = sorts.filter((oldSort) => oldSort.id !== id);
    setSorts(newSorting);
  };

  // TODO: Make more like FilterPopover
  const handleChangeSort = <T,>(id: string, newValue: T, key: string): void => {
    const newSorting = sorts.map((oldSort) => {
      if (oldSort.id !== id) return oldSort;
      return {
        ...oldSort,
        [key]: newValue,
      };
    });
    setSorts(newSorting);
  };

  return (
    <>
      <StyledDropdownButton
        as={PopoverDisclosure}
        state={state}
        active={sortCount > 0}
        leadingIcon={<StyledSortIcon />}
        trailingIcon={<SelectorIcon />}
        placeholder="No Sort"
        value={
          sortCount > 0 ? (
            <>
              Sorted By{' '}
              <Typography as="span" fontSize="sm" fontWeight="medium">
                {sortCount}
              </Typography>{' '}
              {sortCount === 1 ? 'Field' : 'Fields'}
            </>
          ) : undefined
        }
      />
      <StyledPopover state={state} label="Sort popover">
        <Stack direction="column" gap={2.5}>
          <StyledPopoverTitle fontSize="sm" as="span" fontWeight="normal">
            {sortCount === 0 ? (
              <>No sorting is applied</>
            ) : (
              <>
                Sort{' '}
                <StyledPopoverTitleSemibold fontSize="sm" as="span" fontWeight="medium">
                  {itemsDescription}
                </StyledPopoverTitleSemibold>
              </>
            )}
          </StyledPopoverTitle>
          <StyledDraggableMenu<ColumnName>
            onChange={(newFieldIds: ColumnName[]): void => {
              const oldSorts = sorts || [];
              const newSorting = newFieldIds
                .map((id) => oldSorts.find((sort, index) => `${sort.id}-${index}` === id))
                .filter((sort): sort is Sorting<ColumnName> => sort !== undefined);
              setSorts(newSorting);
            }}
          >
            {sorts.map((sort, index) => (
              <StyledDraggableMenuItem key={sort.id} id={`${sort.id}-${index}`}>
                <StyledMenuItemInnerContainer gap={2}>
                  <StyledTitle fontWeight="medium" fontSize="sm">
                    {index === 0 ? 'first by' : 'then by'}
                  </StyledTitle>
                  <StyledConfigDropdownMenu
                    // Options are the columns in order, without any which are
                    // already selected in other dropdowns, but *with* the one
                    // selected in *this* dropdown.
                    options={columns
                      .filter(({ name }) => sort.column === name || !sorts.map((s) => s.column).includes(name))
                      .map(({ label, name }) => ({ value: name, label }))}
                    onChange={(columnName: ColumnName): void =>
                      handleChangeSort<ColumnName>(sort.id, columnName, 'column')
                    }
                    value={sort.column}
                  />
                  <SortDirectionDropdownMenu
                    dataType={fieldTypeForColumn(sort.column)}
                    value={sort.direction || null}
                    onChange={(direction): void => handleChangeSort<SortDirection>(sort.id, direction, 'direction')}
                  />
                  <StyledDeleteButton
                    onClick={(): void => handleDeleteSort(sort.id)}
                    size="base"
                    variant="rsbry"
                    rounded="regular"
                  >
                    <StyledTrashBin />
                  </StyledDeleteButton>
                </StyledMenuItemInnerContainer>
              </StyledDraggableMenuItem>
            ))}
          </StyledDraggableMenu>
          <Stack direction="row" justifyContent="space-between" gap={4}>
            <DropdownMenu
              as={StyledAddConditionButton}
              onChange={handleAddSort}
              options={columns
                .filter(({ name: value }) => !sorts.map((s) => s.column).includes(value))
                .map(({ label, name }) => ({ value: name, label }))}
              rounded="regular"
              size="xs"
              startIcon={<StyledPlusIcon />}
              variant="primary"
              value={null}
            >
              {sortCount > 0 ? 'Add another sort' : 'Add a sort'}
            </DropdownMenu>

            {sortCount > 0 && (
              <StyledResetButton onClick={resetSorts} size="xs" variant="white" rounded="regular">
                Reset sort
              </StyledResetButton>
            )}
          </Stack>
        </Stack>
      </StyledPopover>
    </>
  );
};
